import React, { useState } from 'react';
import { functions } from '../firebase-config';
import { httpsCallable } from 'firebase/functions';

const STATUS_CONFIG = {
  pending: { label: 'Needs your review', bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
  pending_review: { label: 'Needs your review', bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
  approved: { label: 'Approved', bg: '#d1fae5', color: '#065f46', dot: '#10b981' },
  converted_to_tenant: { label: 'Converted To Tenant', bg: '#dcfce7', color: '#166534', dot: '#22c55e' },
  rejected: { label: 'Rejected', bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
  archived: { label: 'Archived', bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af' },
  follow_up: { label: 'Follow Up', bg: '#fef08a', color: '#854d0e', dot: '#eab308' },
  extraction_failed: { label: 'Processing Error', bg: '#fce7f3', color: '#9d174d', dot: '#ec4899' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: cfg.bg, color: cfg.color,
      padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, display: 'inline-block' }} />
      {cfg.label}
    </span>
  );
}

function getCreditScoreTone(score) {
  const safeScore = Number(score);
  if (!Number.isFinite(safeScore)) return null;
  const normalized = Math.max(0, Math.min(1, (safeScore - 300) / 550));
  const hue = 4 + (normalized * 126);

  let note = 'Needs review';
  if (safeScore >= 740) note = 'Excellent range';
  else if (safeScore >= 670) note = 'Strong range';
  else if (safeScore >= 580) note = 'Fair range';

  return {
    bg: `hsl(${hue} 85% 95%)`,
    border: `hsl(${hue} 70% 78%)`,
    text: `hsl(${hue} 70% 28%)`,
    note,
  };
}

function getIncomeTone(amount) {
  const safeAmount = Number(amount);
  if (!Number.isFinite(safeAmount)) return null;
  if (safeAmount >= 6000) return { bg: '#ecfdf5', border: '#86efac', text: '#166534', note: 'Stronger monthly income' };
  if (safeAmount >= 3500) return { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', note: 'Moderate monthly income' };
  return { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', note: 'Lower monthly income' };
}

function StatChip({ icon, label, value, tone }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{
      background: tone?.bg || '#f8fafc', border: `1px solid ${tone?.border || '#e2e8f0'}`, borderRadius: 10,
      padding: '10px 14px', minWidth: 120,
    }}>
      <p style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, margin: '0 0 2px 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {icon} {label}
      </p>
      <p style={{ color: tone?.text || '#1e293b', fontSize: 16, fontWeight: 700, margin: 0 }}>{value}</p>
      {tone?.note && (
        <p style={{ color: tone?.text || '#475569', fontSize: 11, fontWeight: 600, margin: '4px 0 0 0' }}>
          {tone.note}
        </p>
      )}
    </div>
  );
}

/**
 * ApplicantCard
 * Renders a single applicant for the Owner Dashboard queue.
 * Props:
 *   applicant   - Firestore applicant document data
 *   applicantId - Firestore document ID
 *   onUpdated   - optional callback after approve/reject
 */
export default function ApplicantCard({ applicant, applicantId, onUpdated, ownerUnits = [] }) {
  const [loading, setLoading] = useState(null); // 'approve' | 'reject' | 'follow_up' | 'delete' | null
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [followUpComments, setFollowUpComments] = useState('');
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', msg }
  const [unitMode, setUnitMode] = useState(ownerUnits.length > 0 ? 'existing' : 'new');
  const [selectedUnitId, setSelectedUnitId] = useState(ownerUnits[0]?.id || '');
  const [newUnitData, setNewUnitData] = useState({
    unitNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    monthlyRent: '',
    leaseStart: '',
    leaseEnd: '',
  });

  const data = applicant.extractedData || {};
  const createdAt = applicant.createdAt?.toDate?.() || new Date();
  const isPending = ['pending', 'pending_review'].includes(applicant.status);
  const creditScoreTone = getCreditScoreTone(data.creditScore);
  const incomeTone = getIncomeTone(data.monthlyIncome);

  const resetApproveState = () => {
    setApproveModalOpen(false);
    setUnitMode(ownerUnits.length > 0 ? 'existing' : 'new');
    setSelectedUnitId(ownerUnits[0]?.id || '');
    setNewUnitData({
      unitNumber: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      monthlyRent: '',
      leaseStart: '',
      leaseEnd: '',
    });
  };

  const handleApprove = async () => {
    setLoading('approve');
    setFeedback(null);
    try {
      const fn = httpsCallable(functions, 'convertApplicantToTenant');
      const response = await fn({
        applicantId,
        unitMode,
        existingUnitId: unitMode === 'existing' ? selectedUnitId : null,
        newUnit: unitMode === 'new' ? newUnitData : null,
      });
      const emailed = response?.data?.emailSent;
      setFeedback({
        type: 'success',
        msg: `Applicant converted to tenant${response?.data?.unitNumber ? ` and assigned to unit ${response.data.unitNumber}` : ''}.${emailed ? ' An invitation email was sent to the tenant (use the same email as on the application when they register).' : ' No invitation email was sent (missing or invalid email on the application). Share a tenant invite link for this unit from your dashboard, or resubmit after correcting the email.'}`,
      });
      resetApproveState();
      if (onUpdated) onUpdated();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message || 'Failed to approve applicant.' });
    } finally {
      setLoading(null);
    }
  };

  const handleReject = async () => {
    const isConvertedArchive = applicant.status === 'converted_to_tenant';
    if (isConvertedArchive) {
      if (!window.confirm('Remove this application from your applicants list? The tenant stays on your roster; this only hides the packet from your queue.')) {
        return;
      }
    } else if (
      !window.confirm(
        'Are you sure you want to reject this applicant? The listing realtor will receive an email, and this application will be removed from your active queue.'
      )
    ) {
      return;
    }

    setLoading('reject');
    setFeedback(null);
    try {
      const fn = httpsCallable(functions, 'rejectApplicant');
      await fn({ applicantId, reason: rejectionReason });
      setFeedback({
        type: 'success',
        msg: isConvertedArchive
          ? 'Application archived from applicants list. Tenant record remains active.'
          : 'Applicant rejected. The realtor has been notified by email.',
      });
      setRejectModalOpen(false);
      setRejectionReason('');
      if (onUpdated) onUpdated();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message || 'Failed to update application.' });
    } finally {
      setLoading(null);
    }
  };

  const handleFollowUp = async () => {
    setLoading('follow_up');
    setFeedback(null);
    try {
      const fn = httpsCallable(functions, 'followUpApplicant');
      await fn({ applicantId, comments: followUpComments });
      setFeedback({ type: 'success', msg: 'Follow-up sent to realtor.' });
      setFollowUpModalOpen(false);
      setFollowUpComments('');
      if (onUpdated) onUpdated();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message || 'Failed to send follow-up.' });
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteApplicant = async () => {
    const isConverted = applicant.status === 'converted_to_tenant';
    const msg = isConverted
      ? 'Delete this application record permanently? The tenant stays on your roster; only the application packet is removed from your queue (and the PDF is removed from storage when possible).'
      : 'Permanently delete this application? It will be removed for you and the realtor, including the uploaded PDF. This cannot be undone.';
    if (!window.confirm(msg)) return;

    setLoading('delete');
    setFeedback(null);
    try {
      const fn = httpsCallable(functions, 'deleteApplicant');
      await fn({ applicantId });
      if (onUpdated) onUpdated();
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message || 'Failed to delete application.' });
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <div
        data-applicant-id={applicantId}
        style={{
        background: '#fff', borderRadius: 16, padding: '24px 28px',
        border: '1px solid #e5e7eb', marginBottom: 16,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        transition: 'box-shadow 0.2s',
      }}
      >
        {/* ── Header row ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{
                width: 42, height: 42, borderRadius: '50%',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 800, fontSize: 16,
              }}>
                {(data.fullName || '?').charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 style={{ margin: 0, color: '#111827', fontSize: 18, fontWeight: 700 }}>
                  {data.fullName || 'Extracting…'}
                </h3>
                <p style={{ margin: 0, color: '#9ca3af', fontSize: 12 }}>
                  Submitted {createdAt.toLocaleDateString()} by realtor
                </p>
              </div>
            </div>
            <StatusBadge status={applicant.status} />
          </div>

          {applicant.pdfUrl && (
            <a
              href={applicant.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 14px',
                color: '#374151', fontSize: 13, fontWeight: 600, textDecoration: 'none',
                background: '#f9fafb', transition: 'background 0.15s',
              }}
            >
              📄 View Original PDF
            </a>
          )}
        </div>

        {/* ── Stats grid ── */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <StatChip icon="💰" label="Monthly Income" value={data.monthlyIncome > 0 ? `$${data.monthlyIncome?.toLocaleString()}` : null} tone={incomeTone} />
          <StatChip icon="📊" label="Credit Score" value={data.creditScore > 0 ? data.creditScore : null} tone={creditScoreTone} />
          <StatChip icon="🏢" label="Employer" value={data.currentEmployer} />
          <StatChip icon="📅" label="Move-in Date" value={data.proposedMoveInDate} />
        </div>

        {/* ── Feedback banner ── */}
        {feedback && feedback.type === 'error' && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: 8, padding: '10px 14px', marginBottom: 16,
            color: '#991b1b',
            fontSize: 14, fontWeight: 600,
          }}>
            ❌ {feedback.msg}
          </div>
        )}

        {/* ── Action buttons (only for pending/follow_up) ── */}
        {(isPending || applicant.status === 'follow_up') && !feedback && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setApproveModalOpen(true);
                setUnitMode(ownerUnits.length > 0 ? 'existing' : 'new');
                setSelectedUnitId(ownerUnits[0]?.id || '');
              }}
              disabled={loading !== null}
              style={{
                flex: 1, background: loading === 'approve'
                  ? '#6ee7b7' : 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff', border: 'none', borderRadius: 10,
                padding: '12px 0', fontWeight: 700, fontSize: 14, cursor: loading ? 'wait' : 'pointer',
                transition: 'opacity 0.2s', opacity: loading ? 0.7 : 1,
              }}
            >
              {loading === 'approve' ? '⚙️ Approving…' : '✅ Approve & Convert'}
            </button>
            <button
              onClick={() => {
                if (applicant.followUpComments) {
                  setRejectionReason(applicant.followUpComments);
                }
                setRejectModalOpen(true);
              }}
              disabled={loading !== null}
              style={{
                flex: 1, background: loading === 'reject'
                  ? '#fca5a5' : '#fff',
                color: '#dc2626', border: '2px solid #fca5a5', borderRadius: 10,
                padding: '12px 0', fontWeight: 700, fontSize: 14, cursor: loading ? 'wait' : 'pointer',
                transition: 'all 0.2s', opacity: loading ? 0.7 : 1,
              }}
            >
              {loading === 'reject' ? '⚙️ Rejecting…' : '❌ Reject'}
            </button>
            <button
              onClick={() => {
                if (applicant.followUpComments) {
                  setFollowUpComments(applicant.followUpComments);
                }
                setFollowUpModalOpen(true);
              }}
              disabled={loading !== null}
              style={{
                flex: 1, background: loading === 'follow_up'
                  ? '#fef08a' : '#fff',
                color: '#854d0e', border: '2px solid #fcd34d', borderRadius: 10,
                padding: '12px 0', fontWeight: 700, fontSize: 14, cursor: loading ? 'wait' : 'pointer',
                transition: 'all 0.2s', opacity: loading ? 0.7 : 1,
              }}
            >
              {loading === 'follow_up' ? '⚙️ Sending…' : '💬 Follow Up'}
            </button>
            <button
              type="button"
              onClick={handleDeleteApplicant}
              disabled={loading !== null}
              style={{
                flex: 1,
                minWidth: 140,
                background: loading === 'delete' ? '#e5e7eb' : '#fff',
                color: '#991b1b',
                border: '2px solid #fecaca',
                borderRadius: 10,
                padding: '12px 0',
                fontWeight: 700,
                fontSize: 14,
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading === 'delete' ? '⚙️ Deleting…' : '🗑️ Delete'}
            </button>
          </div>
        )}

        {/* ── Archive button for converted applicants ── */}
        {applicant.status === 'converted_to_tenant' && !feedback && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setRejectModalOpen(true);
              }}
              disabled={loading !== null}
              style={{
                flex: 1,
                minWidth: 160,
                background: loading === 'reject'
                  ? '#fca5a5' : '#fff',
                color: '#dc2626', border: '2px solid #fca5a5', borderRadius: 10,
                padding: '12px 0', fontWeight: 700, fontSize: 14, cursor: loading ? 'wait' : 'pointer',
                transition: 'all 0.2s', opacity: loading ? 0.7 : 1,
              }}
            >
              {loading === 'reject' ? '⚙️ Archiving…' : '📁 Archive from Applicants'}
            </button>
            <button
              type="button"
              onClick={handleDeleteApplicant}
              disabled={loading !== null}
              style={{
                flex: 1,
                minWidth: 160,
                background: loading === 'delete' ? '#e5e7eb' : '#fff',
                color: '#991b1b',
                border: '2px solid #fecaca',
                borderRadius: 10,
                padding: '12px 0',
                fontWeight: 700,
                fontSize: 14,
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading === 'delete' ? '⚙️ Deleting…' : '🗑️ Delete record'}
            </button>
          </div>
        )}

        {/* Follow-up comments */}
        {applicant.status === 'follow_up' && applicant.followUpComments && (
          <div style={{
            background: '#fef08a', border: '1px solid #fcd34d', borderRadius: 8,
            padding: '12px 14px', marginBottom: 16, color: '#854d0e', fontSize: 13,
          }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>💬 Follow-up from Owner:</p>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{applicant.followUpComments}</p>
          </div>
        )}

        {/* Already resolved */}
        {!isPending && applicant.status !== 'follow_up' && (
          <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>
            {applicant.status === 'converted_to_tenant'
              ? `✅ Converted to tenant${applicant.unitNumber ? ` for unit ${applicant.unitNumber}` : ''}.`
              : applicant.status === 'approved'
                ? '✅ Approved and added to tenant roster.'
                : applicant.status === 'archived'
                  ? `📁 Archived${applicant.rejectionReason ? ': ' + applicant.rejectionReason : '.'}`
                  : `❌ Rejected${applicant.rejectionReason ? ': ' + applicant.rejectionReason : '.'}`}
          </p>
        )}
      </div>

      {/* ── Reject reason modal ── */}
      {approveModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 620,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#111827', fontSize: 20 }}>Approve and convert to tenant</h3>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
              Choose where {data.fullName || 'this applicant'} should live. You can assign them to an existing unit or create a new one on the spot.
            </p>

            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              <button
                onClick={() => setUnitMode('existing')}
                style={{
                  background: unitMode === 'existing' ? '#e0e7ff' : '#fff',
                  color: unitMode === 'existing' ? '#3730a3' : '#374151',
                  border: `1px solid ${unitMode === 'existing' ? '#a5b4fc' : '#d1d5db'}`,
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Assign Existing Unit
              </button>
              <button
                onClick={() => setUnitMode('new')}
                style={{
                  background: unitMode === 'new' ? '#e0f2fe' : '#fff',
                  color: unitMode === 'new' ? '#075985' : '#374151',
                  border: `1px solid ${unitMode === 'new' ? '#7dd3fc' : '#d1d5db'}`,
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Create New Unit
              </button>
            </div>

            {unitMode === 'existing' ? (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontWeight: 700, fontSize: 12, marginBottom: 8, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Select Unit
                </label>
                <select
                  value={selectedUnitId}
                  onChange={(e) => setSelectedUnitId(e.target.value)}
                  style={{ width: '100%', padding: '11px 14px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14 }}
                >
                  <option value="">Choose a unit...</option>
                  {ownerUnits.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      Unit {unit.unitNumber} - {unit.address}
                    </option>
                  ))}
                </select>
                {ownerUnits.length === 0 && (
                  <p style={{ margin: '10px 0 0 0', color: '#9a3412', fontSize: 13 }}>
                    No existing units are available yet, so create a new one below.
                  </p>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14, marginBottom: 20 }}>
                {[
                  ['unitNumber', 'Unit Number'],
                  ['address', 'Address'],
                  ['city', 'City'],
                  ['state', 'State'],
                  ['zipCode', 'Zip Code'],
                  ['monthlyRent', 'Monthly Rent'],
                  ['leaseStart', 'Lease Start'],
                  ['leaseEnd', 'Lease End'],
                ].map(([field, label]) => (
                  <div key={field} style={{ gridColumn: field === 'address' ? '1 / -1' : 'auto' }}>
                    <label style={{ display: 'block', fontWeight: 700, fontSize: 12, marginBottom: 8, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {label}{['unitNumber', 'address'].includes(field) ? ' *' : ''}
                    </label>
                    <input
                      value={newUnitData[field]}
                      onChange={(e) => setNewUnitData((prev) => ({ ...prev, [field]: e.target.value }))}
                      style={{ width: '100%', padding: '11px 14px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' }}
                      placeholder={label}
                    />
                  </div>
                ))}
              </div>
            )}

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: 700, color: '#1f2937' }}>Tenant record preview</p>
              <p style={{ margin: '4px 0', color: '#475569', fontSize: 14 }}><strong>Name:</strong> {data.fullName || 'N/A'}</p>
              <p style={{ margin: '4px 0', color: '#475569', fontSize: 14 }}><strong>Email:</strong> {data.email || 'Not provided'}</p>
              <p style={{ margin: '4px 0', color: '#475569', fontSize: 14 }}><strong>Phone:</strong> {data.phone || 'Not provided'}</p>
              <p style={{ margin: '4px 0', color: '#475569', fontSize: 14 }}><strong>Move-in:</strong> {data.proposedMoveInDate || 'Not provided'}</p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={resetApproveState}
                style={{
                  flex: 1, background: '#f3f4f6', color: '#374151', border: 'none',
                  borderRadius: 10, padding: '12px 0', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={loading === 'approve' || (unitMode === 'existing' && !selectedUnitId)}
                style={{
                  flex: 1, background: '#10b981', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '12px 0', fontWeight: 700, cursor: 'pointer',
                  opacity: loading === 'approve' || (unitMode === 'existing' && !selectedUnitId) ? 0.7 : 1,
                }}
              >
                {loading === 'approve' ? 'Converting...' : 'Approve And Create Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#111827', fontSize: 18 }}>
              {applicant.status === 'converted_to_tenant' ? 'Archive from Applicants' : 'Reject applicant'}
            </h3>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
              {applicant.status === 'converted_to_tenant'
                ? `Archive ${data.fullName || 'this applicant'} from the applicants list. They will remain as a tenant in your system, but the application record will be moved to the "Archived" tab for record-keeping.`
                : `Reject ${data.fullName || 'this applicant'}? The listing realtor will receive an email with your optional reason below. This application will disappear from your active queue (you can still find it under Archived).`}
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Optional reason (included in email to realtor), e.g. Income does not meet minimum requirements"
              rows={3}
              style={{
                width: '100%', padding: '12px 14px', border: '1px solid #d1d5db',
                borderRadius: 8, fontSize: 14, resize: 'vertical', outline: 'none',
                boxSizing: 'border-box', marginBottom: 20, color: '#1f2937',
              }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setRejectModalOpen(false); setRejectionReason(''); }}
                style={{
                  flex: 1, background: '#f3f4f6', color: '#374151', border: 'none',
                  borderRadius: 10, padding: '12px 0', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={loading === 'reject'}
                style={{
                  flex: 1, background: '#dc2626', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '12px 0', fontWeight: 700, cursor: 'pointer',
                }}
              >
                {loading === 'reject'
                  ? applicant.status === 'converted_to_tenant'
                    ? 'Archiving…'
                    : 'Rejecting…'
                  : applicant.status === 'converted_to_tenant'
                    ? 'Archive from Applicants'
                    : 'Reject applicant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Follow-up modal ── */}
      {followUpModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#111827', fontSize: 18 }}>Follow Up with Realtor</h3>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
              Send questions or comments about {data.fullName || 'this applicant'} back to the realtor.
            </p>
            <textarea
              value={followUpComments}
              onChange={(e) => setFollowUpComments(e.target.value)}
              placeholder="e.g. Can you provide employment verification? What is the employment start date?"
              rows={3}
              style={{
                width: '100%', padding: '12px 14px', border: '1px solid #d1d5db',
                borderRadius: 8, fontSize: 14, resize: 'vertical', outline: 'none',
                boxSizing: 'border-box', marginBottom: 20, color: '#1f2937',
              }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setFollowUpModalOpen(false); setFollowUpComments(''); }}
                style={{
                  flex: 1, background: '#f3f4f6', color: '#374151', border: 'none',
                  borderRadius: 10, padding: '12px 0', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleFollowUp}
                disabled={loading === 'follow_up'}
                style={{
                  flex: 1, background: '#eab308', color: '#854d0e', border: 'none',
                  borderRadius: 10, padding: '12px 0', fontWeight: 700, cursor: 'pointer',
                }}
              >
                {loading === 'follow_up' ? 'Sending…' : 'Send Follow Up'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
